import * as Yup from "yup"
import { Link } from "react-router-dom"
import CreatableSelect from "react-select/creatable"
import { useField, useFormikContext } from "formik"
import type { MultiValue } from "react-select"

import CatchCard from "~/library/CatchCard"
import FormikWrapper from "~/library/FormikWrapper"
import FormikInput from "~/library/FormikInput"
import FormikRadioGroup from "~/library/FormikRadioGroup"
import ImageUpload from "~/library/ImageUpload"
import type Venue from "~/types/Venue"
import type { Image } from "~/types/Image"
import { reactSelectStyles } from "~/utils/reactSelectStyles"

const venueSchema = Yup.object({
  name: Yup.string().required("A venue name is required"),
  currency: Yup.string().required("A currency is required"),
})

interface Option {
  value: string
  label: string
}

const CategoriesSelect = ({
  name,
  label,
  options,
  ...props
}: {
  name: string
  label: string
  options: Option[]
}) => {
  const [field] = useField(name)
  const { setFieldValue } = useFormikContext()

  const handleChange = (selectedOptions: MultiValue<Option>) => {
    setFieldValue(name, selectedOptions ? selectedOptions.map((option) => option.value) : [])
  }

  const value = field.value ? field.value.map((v: string) => ({ value: v, label: v })) : []

  return (
    <div className="form-group mb-3">
      <label className="form-label">{label}</label>
      <CreatableSelect
        isMulti
        onChange={handleChange}
        options={options}
        value={value}
        className="react-select-container"
        classNamePrefix="react-select"
        styles={reactSelectStyles}
        {...props}
      />
    </div>
  )
}

const VenueFormBody = ({
  initialValues,
  allCategoryOptions,
}: {
  initialValues?: Venue
  allCategoryOptions: Option[]
}) => {
  const { setFieldValue } = useFormikContext()

  return (
    <>
      <CatchCard>
        <h4 className="card-title">Venue Details</h4>
        <FormikInput name="name" label="Venue Name" required />
        <FormikInput name="slug" label="Venue Slug" required />
        <label htmlFor="currency" className="form-label">
          Currency (default: USD)
        </label>
        <FormikRadioGroup
          name="currency"
          label="Currency"
          options={[
            { value: "USD", label: "USD" },
            { value: "CAD", label: "CAD" },
          ]}
        />
        <CategoriesSelect name="categories" label="Categories" options={allCategoryOptions} />
      </CatchCard>
      <CatchCard>
        <h4 className="card-title">Address</h4>
        <FormikInput name="address1" label="Address 1" />
        <FormikInput name="address2" label="Address 2" />
        <FormikInput name="city" label="City" />
        <FormikInput name="state" label="State" />
        <FormikInput name="postalCode" label="Postal Code" />
        <FormikInput name="country" label="Country" />
      </CatchCard>
      <CatchCard>
        <h4 className="card-title">Logo</h4>
        <ImageUpload
          currentImageUrl={initialValues?.logoUrl}
          alt={initialValues?.logo?.alt}
          title={initialValues?.logo?.title}
          onUpload={(newImage: Image) => {
            setFieldValue("logo_id", newImage.id)
          }}
        />
      </CatchCard>
      <div className="d-flex">
        <div className="ms-auto">
          <Link className="btn btn-secondary me-2" to="/">
            Cancel
          </Link>
          <button type="submit" className="btn btn-primary">
            Submit
          </button>
        </div>
      </div>
    </>
  )
}

const VenueForm = ({
  onSubmit,
  initialValues,
  isEditing = false,
}: {
  onSubmit: (values: any) => void
  initialValues?: Venue
  isEditing: boolean
}) => {
  const defaultValues = {
    name: "",
    currency: "USD",
    address1: "",
    address2: "",
    city: "",
    state: "",
    postal_code: "",
    country: "",
    categories: [],
    logo_id: null,
  }

  const baseCategories = [
    { value: "Wedding", label: "Wedding" },
    { value: "Corporate", label: "Corporate" },
    { value: "Private", label: "Private" },
  ]

  const existingCategories = (initialValues?.categories || []).map((c: string) => ({
    value: c,
    label: c,
  }))

  const allCategoryOptions = [...baseCategories]
  existingCategories.forEach((existingCat: Option) => {
    if (!allCategoryOptions.find((opt) => opt.value === existingCat.value)) {
      allCategoryOptions.push(existingCat)
    }
  })

  const formValues =
    isEditing && initialValues
      ? { ...initialValues, logo_id: initialValues.logo?.id }
      : defaultValues

  return (
    <FormikWrapper
      onSubmit={onSubmit}
      validationSchema={venueSchema}
      initialValues={formValues}
      enableReinitialize
    >
      <VenueFormBody initialValues={initialValues} allCategoryOptions={allCategoryOptions} />
    </FormikWrapper>
  )
}

export default VenueForm
